import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Paper, 
  TextField, 
  IconButton, 
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const JpCfdDownload = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleFetchData = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    
    try {
      const response = await fetch('https://ksk-aircloud.com/api/jp_cfd/api/indicators/jp225/all/');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!data) return;
    
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 3 }}>
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 4 }}>
        <Box textAlign="center">
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            JP225 技術指標データ
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 4, px: isMobile ? 1 : 0 }}
          >
            最新のJP225（日経225）データから全技術指標を取得できます。
          </Typography>
          
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleFetchData}
            disabled={loading}
            sx={{
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 3 : 4,
              fontSize: isMobile ? '1rem' : '1.1rem',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              }
            }}
          >
            {loading ? 'データ取得中...' : '技術指標を取得'}
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {data && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 1 : 0
              }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  取得結果
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopy}
                  sx={{
                    minWidth: isMobile ? '100%' : 'auto',
                    backgroundColor: copied ? '#4caf50' : 'transparent',
                    color: copied ? 'white' : 'inherit',
                    '&:hover': {
                      backgroundColor: copied ? '#45a049' : 'rgba(0,0,0,0.04)',
                    }
                  }}
                >
                  {copied ? 'コピー完了!' : '結果をコピー'}
                </Button>
              </Box>
              
              <TextField
                multiline
                rows={isMobile ? 20 : 25}
                value={JSON.stringify(data, null, 2)}
                variant="outlined"
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    backgroundColor: '#f5f5f5',
                    '& .MuiInputBase-input': {
                      padding: isMobile ? 1 : 2,
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                  }
                }}
              />
            </Box>
          )}
          
          <Box sx={{ mt: 4, textAlign: 'left', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              含まれる技術指標：
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              mt: 2
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • 移動平均線 (SMA)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  期間: 5, 20, 60
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • 指数移動平均線 (EMA)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  期間: 8, 13, 21
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • MACD
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  設定: 12-26-9, 9-12-6, 3-10-16
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • RSI
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  期間: 14, 9
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • ATR
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  期間: 14
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  • ボリンジャーバンド
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  設定: 20±2σ, 14±1.5σ
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default JpCfdDownload; 